'use client';

import { getRedirectUrl } from '@/features/redirect/api/getRedirectUrl';
import { getCookieValue, setCookie } from '@/utils/cookie';
import { newDate } from '@/utils/date';
import { differenceInSeconds } from 'date-fns';
import { notFound } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

// リファラ制限してる場合の処理コンポーネント
function CheckReferrer({
  approveReferrer,
  lotteryId,
  startAt,
  endAt,
  isPreview = false,
  referrer = '',
  onSuccess = () => {},
}: {
  approveReferrer?: string;
  lotteryId: number;
  startAt: string;
  endAt?: string;
  isPreview?: boolean;
  referrer?: string;
  onSuccess?: () => void;
}) {
  const checkingRef = useRef(false);
  const [shouldShowNotFound, setShouldShowNotFound] = useState(false);
  const cookieKey = `approved_referrer_${lotteryId}`;

  // 404エラーが発生した場合はnotFoundを返す
  if (shouldShowNotFound) notFound();

  // URL文字列をパースしてURLオブジェクトを返す
  const getUrl = (url: string): URL | null => {
    try {
      // リファラURLをトリムしてURLオブジェクトに変換
      return new URL(url.trim());
    } catch {
      // リファラURLをパースできない場合はnullを返す
      return null;
    }
  };

  // くじ詳細のリファラURLとヘッダーのリファラチェック
  const checkReferrer = (approveReferrer: string, headerReferrer: string): boolean => {
    // ヘッダーのリファラがない場合はfalse
    if (!headerReferrer) return false;
    // くじ詳細のリファラURLにヘッダーのリファラURLが含まれていない場合はfalse
    if (!approveReferrer.includes(headerReferrer)) {
      return false;
    }
    return true;
  };

  // RedirectKeyによるクッキーチェック
  const checkRedirectKey = (url: string): boolean => {
    // URLの一番最後のパスを取得
    const lastSegment = url.split('/').pop();
    // デバッグ用 (TODO: デバッグ後は必ず消すこと！！！)
    if (lastSegment !== undefined) {
      // biome-ignore lint/suspicious/noConsoleLog: <explanation>
      console.log('lastSegment', lastSegment);
      // biome-ignore lint/suspicious/noConsoleLog: <explanation>
      console.log('getCookieValue(lastSegment)', getCookieValue(lastSegment));
    }
    if (lastSegment === undefined || getCookieValue(lastSegment) !== 'true') {
      // クッキーが存在しないか、中身がtrueでない場合はfalseを返す
      return false;
    }

    return true;
  };

  // urlからリダイレクトキーを取得する
  const getRedirectKeyWithUrl = (url: string): string => {
    const urlObj: URL | null = getUrl(url ?? '');
    // URLが/r/{任意の文字列} の形式の場合は、{任意の文字列}を返す。それ以外はブランクを返す
    return urlObj?.pathname.match(/^\/r\/(.*)$/)?.[1] ?? '';
  };

  // API実行してリダイレクトキーの有効性確認（useEffect内でawaitするためasync IIFEで実行。エラー時はfalseを返して呼び出し元でreturnする）
  const confirmRedirectUrlValidity = async (redirectKey: string): Promise<boolean> => {
    const result = await getRedirectUrl({
      oemServiceId: Number(process.env.NEXT_PUBLIC_SERVICE_ID),
      key: redirectKey,
    }).catch(() => {
      console.error('confirmRedirectUrlValidity: redirectUrl api error');
      return undefined;
    });
    if (!result) {
      return false;
    }
    return true;
  };

  // くじ詳細のリファラURLあり + ヘッダーのリファラからリダイレクトキー取得済み時のチェック
  const validateApproveReferrerRedirectFlow = async (
    approveReferrer: string,
    headerReferrer: string,
  ): Promise<boolean> => {
    // くじ詳細のリファラURLからリダイレクトキーを取得する
    const approveReferrerRedirectKey = getRedirectKeyWithUrl(approveReferrer);

    // くじ詳細のリファラURLからリダイレクトキーを取得できなかった場合(パターン５)
    if (!approveReferrerRedirectKey) {
      return false;
    }

    // くじ詳細のリファラURLからリダイレクトキーを取得できた場合(パターン４)
    // リファラチェックに引っ掛かれば404エラー
    if (!checkReferrer(approveReferrer, headerReferrer)) {
      return false;
    }

    // クッキーの有効性チェックに引っ掛かれば404エラー
    if (!checkRedirectKey(approveReferrer)) {
      return false;
    }

    // リダイレクトURLの有効性確認（完了を待ってからクッキーをセットするためawait）、falseが返った場合は404エラー
    return confirmRedirectUrlValidity(approveReferrerRedirectKey);
  };

  // くじ詳細のリファラURLなし + ヘッダーのリファラからリダイレクトキー取得済み時のチェック
  const validateHeaderReferrerRedirectFlow = async (
    headerReferrer: string,
    headerReferrerRedirectKey: string,
  ): Promise<boolean> => {
    // クッキーの有効性チェックに引っ掛かれば404エラー
    if (!checkRedirectKey(headerReferrer)) {
      return false;
    }
    // リダイレクトURLの有効性確認（完了を待ってからクッキーをセットするためawait）、falseが返った場合は404エラー
    return confirmRedirectUrlValidity(headerReferrerRedirectKey);
  };

  // useEffect内で実行する関数
  useEffect(() => {
    // プレビュー時は無条件で通す
    if (isPreview) {
      onSuccess();
      return;
    }
    if (checkingRef.current) {
      // 二重チェック防止用の判定のためonSuccessは呼び出さない
      return;
    }
    checkingRef.current = true;
    // リロード対策用のクッキーが存在していてtrueの場合は処理を終了
    if (getCookieValue(cookieKey) === 'true') {
      onSuccess();
      return;
    }

    // ヘッダーのリファラからリダイレクトキーを取得する
    const headerReferrerRedirectKey: string = getRedirectKeyWithUrl(referrer);

    // useEffect内でawaitを使用するため、async関数で包んで呼び出す
    (async () => {
      if (headerReferrerRedirectKey) {
        // ヘッダーのリファラからリダイレクトキーを取得できた場合

        if (approveReferrer && !(await validateApproveReferrerRedirectFlow(approveReferrer, referrer))) {
          // くじ詳細のリファラURLが設定されている場合
          // かつ
          // パターン4・5のいずれかのチェックで失敗した場合

          // 404エラーを表示
          setShouldShowNotFound(true);
          return;
        }

        if (!approveReferrer && !(await validateHeaderReferrerRedirectFlow(referrer, headerReferrerRedirectKey))) {
          // くじ詳細のリファラURLが設定されていない場合
          // かつ
          // パターン2のチェックで失敗した場合

          // 404エラーを表示
          setShouldShowNotFound(true);
          return;
        }
      } else {
        // ヘッダーのリファラからリダイレクトキーを取得できなかった場合

        if (!approveReferrer) {
          // くじ詳細のリファラURLが設定されていない場合は処理を終了（パターン１）

          // 成功時のコールバックを呼び出す
          onSuccess();
          return;
        }

        // くじ詳細のリファラURLからリダイレクトキーを取得する
        const approveReferrerRedirectKey: string = getRedirectKeyWithUrl(approveReferrer);

        if (approveReferrerRedirectKey) {
          // くじ詳細のリファラURLからリダイレクトキーを取得できた場合（パターン３）

          // 404エラーを表示
          setShouldShowNotFound(true);
          return;
        }

        // くじ詳細のリファラURLからリダイレクトキーを取得できなかった場合はリファラチェック(パターン６)
        if (!checkReferrer(approveReferrer, referrer)) {
          // リファラチェックに引っ掛かれば404エラー

          // 404エラーを表示
          setShouldShowNotFound(true);
          return;
        }
      }

      // リロード対策にクッキーをセット
      // maxAgeはくじ開催期間と同じ
      const hasPeriod = endAt && startAt;
      const maxAge = hasPeriod ? differenceInSeconds(newDate(endAt), newDate(startAt)) : 0;
      setCookie(cookieKey, 'true', maxAge);
      // 成功時のコールバックを呼び出す
      onSuccess();
    })();
  }, []);

  return <></>;
}

export default CheckReferrer;
